import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { DataProvider } from './context/DataContext';
import { Taller3DProvider } from './context/Taller3DContext';
import { ClientesProvider } from './context/ClientesContext';
import { PerfilProvider } from './context/PerfilContext';
import { PlusProvider } from './context/PlusContext';
import { ThemeProvider } from './context/ThemeContext';
import { ConfirmarProvider } from './context/ConfirmarContext';
import { PrivateRoute } from './components/PrivateRoute';
import { Layout } from './components/Layout';
import { ConCuentaActiva, ConModulo } from './components/Guardas';
import { Login } from './pages/Login';
import { Signup } from './pages/Signup';
import { Inicio } from './pages/Inicio';
import { Bienvenida } from './pages/Bienvenida';
import { Dashboard } from './pages/Dashboard';
import { Apuntar } from './pages/Apuntar';
import { Transacciones } from './pages/Transacciones';
import { Presupuestos } from './pages/Presupuestos';
import { Configuracion } from './pages/Configuracion';
import { TraspasosPeriodicos } from './pages/TraspasosPeriodicos';
import { Informes } from './pages/Informes';
import { Inversiones } from './pages/Inversiones';
import { MigracionPresupuestos } from './pages/MigracionPresupuestos';
import { Calculadora3D } from './pages/taller3d/Calculadora3D';
import { Presupuestos3D } from './pages/taller3d/Presupuestos3D';
import { Clientes } from './pages/clientes/Clientes';
import { FichaCliente } from './pages/clientes/FichaCliente';
import { PrestamosProvider } from './context/PrestamosContext';
import { Prestamos } from './pages/prestamos/Prestamos';
import { AMediasProvider } from './context/AMediasContext';
import { SincronizacionAMediasProvider } from './context/SincronizacionAMediasContext';
import { AvisosAMediasProvider } from './context/AvisosAMediasContext';
import { Planes } from './pages/amedias/Planes';
import { DetallePlan } from './pages/amedias/DetallePlan';
import { UnirsePlan } from './pages/amedias/UnirsePlan';
import { TareasProvider } from './context/TareasContext';
import { Semana } from './pages/tareas/Semana';
import { Casas } from './pages/tareas/Casas';
import { Compra } from './pages/tareas/Compra';
import { UnirseCasa } from './pages/tareas/UnirseCasa';
import { AvisosTareas } from './components/tareas/AvisosTareas';
import { PreferenciasCuenta } from './components/PreferenciasCuenta';
import { Ayuda } from './pages/Ayuda';
import { Privacidad } from './pages/Privacidad';
import { EnlacesEntrantes } from './components/EnlacesEntrantes';
import { RecordatoriosGastos } from './components/RecordatoriosGastos';
import { ArregloTecladoIOS } from './components/ArregloTecladoIOS';
import { useAuth } from './context/AuthContext';
import { esApp } from './utils/plataforma';
import { BloqueoProvider } from './context/BloqueoContext';

/** Página privada: sesión iniciada y cuenta con algún módulo activado. */
const Privada: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <PrivateRoute>
    <ConCuentaActiva>
      <Layout>{children}</Layout>
    </ConCuentaActiva>
  </PrivateRoute>
);

/** Página de un módulo: además, que ese módulo esté activado en la cuenta. */
const DeModulo: React.FC<{ modulo: string; children: React.ReactNode }> = ({ modulo, children }) => (
  <PrivateRoute>
    <ConCuentaActiva>
      <Layout>
        <ConModulo modulo={modulo}>{children}</ConModulo>
      </Layout>
    </ConCuentaActiva>
  </PrivateRoute>
);

/**
 * La raíz: con sesión, el hub de módulos; sin ella, la página de presentación
 * en la web. En la app de iOS ya se sabe qué es: va directo a entrar.
 */
const Raiz: React.FC = () => {
  const { user, loading } = useAuth();
  if (loading || user || esApp) return <Privada><Inicio /></Privada>;
  return <Bienvenida />;
};

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
      <ConfirmarProvider>
        <AuthProvider>
        <BloqueoProvider>
          <EnlacesEntrantes />
          <ArregloTecladoIOS />
          <PerfilProvider>
          <RecordatoriosGastos />
          <PlusProvider>
            <DataProvider>
              <ClientesProvider>
              <Taller3DProvider>
              <PrestamosProvider>
              <AMediasProvider>
              <TareasProvider>
              <AvisosTareas />
              <PreferenciasCuenta />
              <SincronizacionAMediasProvider>
              <AvisosAMediasProvider>
                <Routes>
                {/* ── Público ──────────────────────────────────────────── */}
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/ayuda" element={<Ayuda />} />
                <Route path="/privacidad" element={<Privacidad />} />

                {/* ── Hub de módulos ───────────────────────────────────── */}
                <Route path="/" element={<Raiz />} />

                {/* ── Módulo: Finanzas ─────────────────────────────────── */}
                <Route path="/finanzas" element={<Navigate to="/finanzas/dashboard" replace />} />
                <Route path="/finanzas/dashboard" element={<DeModulo modulo="finanzas"><Dashboard /></DeModulo>} />
                {/* Atajos de iOS y Wallet: el formulario de gasto rellenado (utils/apuntarRapido) */}
                <Route path="/apuntar" element={<DeModulo modulo="finanzas"><Apuntar /></DeModulo>} />
                <Route path="/finanzas/transacciones" element={<DeModulo modulo="finanzas"><Transacciones /></DeModulo>} />
                <Route path="/finanzas/presupuestos" element={<DeModulo modulo="finanzas"><Presupuestos /></DeModulo>} />
                <Route path="/finanzas/periodicos" element={<DeModulo modulo="finanzas"><TraspasosPeriodicos /></DeModulo>} />
                <Route path="/finanzas/informes" element={<DeModulo modulo="finanzas"><Informes /></DeModulo>} />
                <Route path="/finanzas/inversiones" element={<DeModulo modulo="finanzas"><Inversiones /></DeModulo>} />
                <Route path="/finanzas/migracion-presupuestos" element={<DeModulo modulo="finanzas"><MigracionPresupuestos /></DeModulo>} />

                {/* ── Módulo: Taller 3D ────────────────────────────────── */}
                <Route path="/taller3d" element={<Navigate to="/taller3d/calculadora" replace />} />
                <Route path="/taller3d/calculadora" element={<DeModulo modulo="taller3d"><Calculadora3D /></DeModulo>} />
                <Route path="/taller3d/presupuestos" element={<DeModulo modulo="taller3d"><Presupuestos3D /></DeModulo>} />

                {/* ── Módulo: Clientes ─────────────────────────────────── */}
                <Route path="/clientes" element={<DeModulo modulo="clientes"><Clientes /></DeModulo>} />
                <Route path="/clientes/:id" element={<DeModulo modulo="clientes"><FichaCliente /></DeModulo>} />

                {/* ── Módulo: Préstamos ────────────────────────────────── */}
                <Route path="/prestamos" element={<DeModulo modulo="prestamos"><Prestamos /></DeModulo>} />

                {/* ── Módulo: A medias ─────────────────────────────────── */}
                <Route path="/amedias" element={<DeModulo modulo="amedias"><Planes /></DeModulo>} />
                <Route path="/amedias/:planId" element={<DeModulo modulo="amedias"><DetallePlan /></DeModulo>} />
                {/* Sin guarda de cuenta activa: unirse es lo que activa el módulo */}
                <Route path="/amedias/unirse/:token" element={<PrivateRoute><UnirsePlan /></PrivateRoute>} />

                {/* ── Módulo: Tareas ───────────────────────────────────── */}
                <Route path="/tareas" element={<DeModulo modulo="tareas"><Semana /></DeModulo>} />
                <Route path="/tareas/casas" element={<DeModulo modulo="tareas"><Casas /></DeModulo>} />
                <Route path="/tareas/compra" element={<DeModulo modulo="tareas"><Compra /></DeModulo>} />
                {/* Sin guarda de cuenta activa, como la de A medias */}
                <Route path="/tareas/unirse/:token" element={<PrivateRoute><UnirseCasa /></PrivateRoute>} />

                {/* ── Transversal ──────────────────────────────────────── */}
                <Route path="/configuracion" element={<Privada><Configuracion /></Privada>} />

                {/* ── Rutas antiguas: se mantienen para no romper enlaces ─ */}
                <Route path="/dashboard"            element={<Navigate to="/finanzas/dashboard" replace />} />
                <Route path="/transacciones"        element={<Navigate to="/finanzas/transacciones" replace />} />
                <Route path="/presupuestos"         element={<Navigate to="/finanzas/presupuestos" replace />} />
                <Route path="/traspasos-periodicos" element={<Navigate to="/finanzas/periodicos" replace />} />
                <Route path="/informes"             element={<Navigate to="/finanzas/informes" replace />} />
                <Route path="/inversiones"          element={<Navigate to="/finanzas/inversiones" replace />} />
                <Route path="/migracion-presupuestos" element={<Navigate to="/finanzas/migracion-presupuestos" replace />} />
                <Route path="/categorias"           element={<Navigate to="/configuracion" replace />} />
                <Route path="/admin/usuarios"       element={<Navigate to="/configuracion" replace />} />

                {/* Cualquier otra cosa vuelve al hub */}
                <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </AvisosAMediasProvider>
              </SincronizacionAMediasProvider>
              </TareasProvider>
              </AMediasProvider>
              </PrestamosProvider>
              </Taller3DProvider>
              </ClientesProvider>
            </DataProvider>
          </PlusProvider>
          </PerfilProvider>
        </BloqueoProvider>
        </AuthProvider>
      </ConfirmarProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
